// ============================================================================
// CARD RIPPER — FEEL PARAMETERS (single source of truth)
//
// 이 게임의 튜닝 값 정본. 손맛에 관여하는 숫자와 소리 매핑은 전부 여기 있다.
// 코드 다른 곳에 튜닝 숫자를 두지 마라.
//
// 단위:
//   길이  = world unit (카메라 z=0 평면에서 화면 높이가 WORLD.viewH unit)
//   시간  = 초 (s)   / 속도 = unit/s / 각도 = rad
//   힘/저항 = 무차원 (같은 파일 안에서만 상대값으로 의미가 있다)
// ============================================================================

// **.ts 확장자를 붙인 import 다.** tools/draw-stats.mjs 처럼 Node 가 이 파일을 직접 import 하는데,
// Node ESM 은 확장자를 유추하지 않는다. tsconfig 의 allowImportingTsExtensions 가 이걸 허용한다.
import { CARD_ASPECT, DEFS_BY_RANK, setFaceShape } from './cards.ts';

// 팩·카드의 기준 크기. 화면에 보이는 크기는 여기에 P.world.objectScale 이 곱해진다.
//
// **카드 높이는 폭에서 유도한다.** 개봉 화면이 카드별 앞면 그림(cards/<id>.webp, 1024×1432)을
// 쓰게 되면서 메시 종횡비가 그림과 같아야 해졌다 — 안 맞으면 그림이 눌리거나 늘어난다.
// 폭(5.2)은 손맛 값이라 그대로 두고 높이만 따라간다 (옛 값 7.8 = 비율 1.5).
// CARD_ASPECT 는 레지스트리 메타에서 온다 (cards.data.json 의 cardAspect = 1432/1024).
const CARD_BASE_W = 5.2;
const BASE = { packW: 6.0, packH: 9.0, cardW: CARD_BASE_W, cardH: CARD_BASE_W * CARD_ASPECT, cardThickness: 0.045 };

export const WORLD = {
  viewH: 16, // 화면 세로 world unit (z=0 평면)
  physicsHz: 240,
  cameraFov: 24, // deg. 약한 원근 — 휘어짐/두께를 읽기 위한 최소한
  // 물체 크기만 배율이 걸린다. viewH 는 고정 — px<->unit 환산이 안 변해야
  // 드래그 임계값·속도 임계값의 px 값이 그대로 남는다 (아래 world.objectScale 주석 참고).
  get packW() {
    return BASE.packW * P.world.objectScale;
  },
  get packH() {
    return BASE.packH * P.world.objectScale;
  },
  get cardW() {
    return BASE.cardW * P.world.objectScale;
  },
  get cardH() {
    return BASE.cardH * P.world.objectScale;
  },
  get cardThickness() {
    return BASE.cardThickness * P.world.objectScale;
  },
};

// 한 팩의 카드는 **리셋마다 확률로 뽑는다.** 지시서 §14 는 "순서 고정 — 뽑기가 아니다" 였지만
// 요청으로 실제 게임처럼 바꿨다. 확률은 P.draw 에 있고, 등급은 CARD_KINDS 의 인덱스가 곧 rank 다.
// CARD_KINDS 의 이름은 **등급 폴백 그림의 파일 이름**이기도 하다 (card_common.webp ...) —
// 개봉 화면은 defIds 의 카드 그림을 쓰고, 그게 없을 때만 이 4장으로 돌아간다.
export const CARD_KINDS = ['card_common', 'card_rare', 'card_ar', 'card_ur'] as const;
export type CardKind = (typeof CARD_KINDS)[number];
/**
 * 한 팩의 장수. **2026-09-16 에 8 → 5 로 줄였다 (사용자 요청).**
 *
 * 이 상수에 딸린 것이 많다. 코드에 8 을 다시 흩뿌리지 마라 — 배열 길이·자리 인덱스·z 오프셋은
 * 전부 여기서 유도한다 (`sim.ts` 의 `(k - (CARD_COUNT-1)/2) * T`, `binder-sim.ts` 의 수납 배열 등).
 * 확률표(`drawSlotProbs`)와 경제 기댓값(`tools/econ-stats.mjs`)도 이 값을 그대로 쓴다.
 */
export const CARD_COUNT = 5;

// 현재 팩의 뽑기 결과. **정렬하지 않는다** — 뽑은 순서 그대로 나온다.
// 다만 AR·UR 은 **뒤 draw.rareLateSlots 자리에만** 나온다 (아래 drawSlotProbs 참고).
// 배열 객체는 그대로 두고 내용만 갈아끼운다 (import 한 쪽이 계속 같은 참조를 본다).
// defIds 는 바인더에 꽂을 카드이자 **개봉 화면 앞면 그림의 키**다 (BINDER_DESIGN §7.5 의 다음 작업).
// 등급에 카드가 한 장도 없으면 null 이고, 커밋에서 무시되며 개봉 화면은 등급 그림으로 돌아간다.
export const CARD_DRAW: { kinds: CardKind[]; ranks: number[]; defIds: (string | null)[] } = { kinds: [], ranks: [], defIds: [] };

// 시드가 있으면 리셋해도 항상 같은 카드. 없으면(null) 리셋마다 새로 뽑는다.
// 검증 도구와 A/B 비교는 같은 카드가 나온다는 전제가 필요해서 시드로 고정한다.
let drawSeed: number | null = null;
export function setDrawSeed(s: number | null): void {
  drawSeed = s;
}
export function getDrawSeed(): number | null {
  return drawSeed;
}

/** mulberry32. 외부 라이브러리 금지 규칙이라 직접 넣었다. */
function mulberry32(a: number): () => number {
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * **자리별** 등급 확률표 `[CARD_COUNT][4]`. `rollCards()` 가 쓰는 것과 똑같은 표라
 * 도구(econ-stats / draw-stats)가 기댓값을 여기서 그대로 계산한다 — 로직 복사본이 없다.
 *
 * 규칙 (2026-09-10 사용자 요청, 기존 "자리 제약 없음" 의 번복):
 * **AR·UR 은 뒤 `draw.rareLateSlots` 자리(기본 2 = 4·5번째)에만 나온다.** 첫 장에는 안 나온다.
 *
 * **전체 기대 장수는 그대로 유지한다.** 자리를 L 개로 좁힌 만큼 그 자리의 AR·UR 확률을
 * `CARD_COUNT / L` 배로 올린다 (기본값이면 2.5배 → AR 15.2% / UR 1.5%). 그래서
 *   E[AR] = L × (p_AR × CARD_COUNT/L) = CARD_COUNT × p_AR 장 = 제약 전과 같다  (UR 도 같다)
 * 이고 **팩의 판매 기댓값이 자리 제약 때문에 변하지는 않는다.** (장수 자체가 8 → 5 로 줄어든 만큼은 준다 —
 * 그건 자리 제약이 아니라 CARD_COUNT 의 결과다. `tools/econ-stats.mjs` 참고.)
 *
 * **`rareLateSlots` 를 2 로 둔 이유 (8장 → 5장에서도 그대로).** 이 값은 비율이 아니라 **사람이 세는 장수**다 —
 * "마지막 두 장이 남았다"가 손에 남는 감각이라 5장에서도 2 가 맞다. 1 로 줄이면 AR·UR 확률이 전부
 * 마지막 한 자리에 몰려 그 자리의 AR 이 30% 가 된다 (사실상 "마지막 장은 특별하다"가 대본이 된다).
 *
 * 앞자리는 커먼:레어를 원래 비율(70:22)로 정규화해서 채운다.
 *
 * 예외 두 가지:
 * - `rareLateSlots = 0` 또는 `CARD_COUNT` 면 제약이 없는 것과 같다 (0 = AR·UR 안 나옴, 5 = 옛 규칙).
 * - **커먼·레어 가중치가 둘 다 0** 이면 앞자리에 놓을 등급이 없다 → 제약을 포기하고 옛 규칙으로 뽑는다.
 * - 올린 AR+UR 확률의 합이 1 을 넘으면 (슬라이더를 극단으로 밀었을 때) 둘을 비율대로 눌러 1 에 맞춘다.
 *   이때만 기대 장수가 줄어든다.
 */
export function drawSlotProbs(): number[][] {
  const d = P.draw;
  const w = [d.weightCommon, d.weightRare, d.weightAr, d.weightUr].map((x) => Math.max(0, x));
  const sum = w[0] + w[1] + w[2] + w[3]; // 합이 100 이 아니어도 정규화된다
  const base = sum > 0 ? w.map((x) => x / sum) : [1, 0, 0, 0];
  const n = CARD_COUNT;
  const L = Math.max(0, Math.min(n, Math.round(d.rareLateSlots)));
  const cr = base[0] + base[1];
  const out: number[][] = [];
  if (cr <= 0 || L >= n) {
    for (let i = 0; i < n; i++) out.push(base.slice());
    return out;
  }
  const early = [base[0] / cr, base[1] / cr, 0, 0];
  let late = early;
  if (L > 0) {
    let a = base[2] * (n / L);
    let u = base[3] * (n / L);
    const s = a + u;
    if (s > 1) {
      a /= s;
      u /= s;
    }
    const rest = 1 - a - u;
    late = [(base[0] / cr) * rest, (base[1] / cr) * rest, a, u];
  }
  for (let i = 0; i < n; i++) out.push(i >= n - L ? late.slice() : early.slice());
  return out;
}

/**
 * CARD_COUNT 장을 다시 뽑는다. 장당 독립이고 **보장·천장 없다** — 전부 커먼일 수도, UR 이 여러 장일 수도 있다.
 * 바뀐 것은 자리 제약뿐이다: AR·UR 은 뒤 `draw.rareLateSlots` 자리에만 나온다 (drawSlotProbs 주석).
 */
export function rollCards(): void {
  const rng = drawSeed === null ? Math.random : mulberry32(drawSeed | 0);
  const probs = drawSlotProbs();
  CARD_DRAW.kinds.length = 0;
  CARD_DRAW.ranks.length = 0;
  for (let i = 0; i < CARD_COUNT; i++) {
    // **자리마다 rng() 를 정확히 한 번** 쓴다. 횟수가 자리마다 달라지면 같은 시드가
    // 다른 수열을 내고 rare-cue / draw-stats 의 전제가 무너진다 (아래 defId 루프 주석과 같은 이유).
    const pr = probs[i];
    let r = rng();
    let k = 0;
    while (k < 3 && r >= pr[k]) r -= pr[k++];
    CARD_DRAW.kinds.push(CARD_KINDS[k]);
    CARD_DRAW.ranks.push(k);
  }
  // ---- 등급 안에서 어느 카드인가 (균등). **반드시 등급 루프가 전부 끝난 뒤에** rng 를 더 쓴다.
  // 사이에 끼우면 같은 시드가 다른 등급 수열을 내고 rare-cue / draw-stats 의 전제가 무너진다 (§3.6).
  CARD_DRAW.defIds.length = 0;
  for (let i = 0; i < CARD_COUNT; i++) {
    const list = DEFS_BY_RANK[CARD_DRAW.ranks[i]];
    if (!list || list.length === 0) {
      CARD_DRAW.defIds.push(null);
      continue;
    }
    CARD_DRAW.defIds.push(list[Math.min(list.length - 1, Math.floor(rng() * list.length))].id);
  }
}

export interface Params {
  world: {
    // 팩·카드를 화면에서 키우는 배율. 커지는 것은 **물체의 world unit 크기**뿐이고
    // 화면(viewH=16)은 안 건드린다. 그래서 1 unit 이 몇 px 인지가 안 변하고,
    // swipe.threshold / flickSpeed / bendLimit / cut.penetrationDepth 처럼
    // unit 으로 적힌 손가락 임계값들이 전부 같은 px 값으로 남는다.
    // (viewH 를 줄여서 키우면 이 값들이 전부 다른 px 이 되어 손맛이 바뀐다.)
    // 바뀐 크기는 지오메트리를 다시 만들어야 해서, 이미 자른 뒤라면 리셋(R) 해야 반영된다.
    objectScale: number;
  };
  draw: {
    // 장당 독립 확률의 가중치. 보장·천장 없음. 합이 100 이 아니어도 정규화해서 쓴다.
    // 슬라이더로 바꾼 값은 다음 리셋(R)부터 반영된다.
    weightCommon: number;
    weightRare: number;
    weightAr: number;
    weightUr: number;
    // **AR·UR 이 나올 수 있는 뒤쪽 자리 수.** 2 = 4·5번째에만. 0 = AR·UR 안 나옴 / 5 = 자리 제약 없음(옛 규칙).
    // 좁힌 만큼 그 자리의 확률을 올려 **전체 기대 장수를 유지한다** (drawSlotProbs 주석).
    rareLateSlots: number;
  };
  cut: {
    perfFrac: number; // 절취선 y (팩 상단에서 아래로, 0..1). 애셋 측정값으로 덮어씀
    bandHalf: number; // 절취선 근처 드래그 시작 허용 반높이 (unit)
    bladeStiffness: number; // 손가락→칼날 스프링 강성 (힘/unit)
    bladeDamping: number; // 칼날 점성 (힘/(unit/s))
    penetrationResistance: number; // 관통 저항 (칼끝이 들어가는 순간)
    penetrationDepth: number; // 관통 구간 길이 (unit)
    reentryFactor: number; // 손 뗐다 다시 이어 자를 때 관통 저항 배율
    baseResistance: number; // 진행 중 기본 저항 (동마찰)
    stickRatio: number; // 정지 저항 / 진행 저항. >1 이면 느린 드래그에서 stick-slip 발생
    jitterAmount: number; // 저항 불규칙 정도 (0..1)
    jitterFreq: number; // 저항 불규칙의 공간 주파수 (cycle/unit)
    velEffect: number; // 속도가 저항에 미치는 영향 (배율/ (unit/s))
    lineJitter: number; // 절단선 지글거림 폭 (unit)
    lineJitterFreq: number; // 절단선 지글거림 주파수 (cycle/unit)
    gapAmount: number; // 틈 벌어짐 (unit)
    gapRamp: number; // 칼날 뒤로 틈이 완전히 벌어지기까지 거리 (unit)
    gapVelEffect: number; // 느린 속도 추정이 틈 폭에 미치는 영향
    bulgeAmount: number; // 칼날 앞 국소 융기 높이 (unit)
    bulgeRadius: number; // 융기 반경 (unit)
    bulgeLead: number; // 융기 중심이 칼날보다 앞선 거리 (unit)
    directionDeadzone: number; // 진행 방향 결정 전 무시 거리 (unit)
  };
  open: {
    topFlySpeed: number; // 상단 조각 초기 상승 속도 (unit/s)
    topSpin: number; // 상단 조각 회전 속도 (rad/s), 마지막 드래그 방향 부호
    topGravity: number; // 상단 조각 중력 (unit/s^2)
    topDriftFactor: number; // 마지막 드래그 x속도가 상단 조각 x속도에 실리는 비율
    bodySinkDistance: number; // 하단 몸통 하강 거리 (unit). 화면 밖(viewH/2 아래)까지 내려야 카드 뭉치를 안 가린다
    bodySinkSpeed: number; // 하단 하강 응답 속도 (1/s). 낮을수록 무겁다
    bodySinkDamping: number; // 하단 감쇠비 (1 = 임계)
    cardDelay: number; // 카드 등장 지연 (s)
    cardStagger: number; // 카드 한 장당 추가 지연 (s)
    cardRiseSpeed: number; // 카드 상승 응답 속도 (1/s)
    cardRiseDamping: number; // 카드 상승 감쇠비 (<1 이면 살짝 튕김)
    cardStartDepth: number; // 팩 내부 시작 위치 (스택 목표에서 아래로, unit)
    stackY: number; // 카드 뭉치 목표 y (unit)
    spreadPos: number; // 뭉치 어긋남 위치 (unit). 0 = 아래 카드가 뒤로 삐져나오지 않는다
    spreadAngle: number; // 뭉치 어긋남 각도 (rad). 0 = 완전히 겹친다
    //   ※ 지시서 §5 는 "조금씩 어긋나 보여야 여러 장으로 읽힌다" 였지만, 요청으로 0 으로 뒀다.
    timeScale: number; // 전체 소요 시간 배율 (1 = 기본, 커질수록 느림)
    // 카드 앞면 그림을 GPU 에 올릴 때 줄이는 폭 (px). 한 팩(CARD_COUNT 장)이 동시에 보이므로
    // 원본(1024w)을 그대로 올리면 장당 5.9MB 가 된다. 화면 표시 폭은 390×844 에서 ≈329px 이라 2배(DPR 2)로 잡았다.
    // 바꾸면 갖고 있던 개봉용 텍스처를 **전부 버리고 다시 만든다** (CardTextureCache.requestOpen).
    cardTexWidth: number;
  };
  flip: {
    duration: number; // 회전 시간 (s), 첫 도달 기준
    overshoot: number; // 지나쳤다 돌아오는 양 (0..0.5, 각도 비율)
    tapInfluence: number; // 탭 위치가 회전축 기울기에 미치는 영향 (rad, 화면 끝 기준)
    rollReturnSpeed: number; // 기울어진 뭉치가 수평으로 돌아오는 응답 속도 (1/s)
  };
  swipe: {
    stickResistance: number; // 붙어 있을 때 저항 (정마찰)
    slipResistance: number; // 미끄러질 때 저항 (동마찰). stick보다 작아야 함
    bendStiffness: number; // 휘어짐 강성 (힘/unit)
    bendVisual: number; // 휘어짐을 화면에 그리는 배율. 0 = 카드가 평평하게 유지된다.
    //   ※ bend 값 자체는 걸림 풀림을 만드는 힘 누적값이라 없앨 수 없다. 이 값은 표현만 끈다.
    bendLimit: number; // 휘어짐 한계 (unit)
    bendLimitFrac: number; // 접점 위 길이 대비 휘어짐 한계 비율
    mass: number; // 카드 무게 (미끄러질 때 가속 관성)
    threshold: number; // 손 뗄 때 날아가는 임계 거리 (unit, 카드 이동 + 휘어짐)
    flickSpeed: number; // 임계 미달이어도 이 속도 이상으로 뗐으면 날아감 (unit/s)
    returnSpeed: number; // 복귀 응답 속도 (1/s)
    returnBounce: number; // 복귀 튕김 (감쇠비, 낮을수록 더 튕김)
    bendRelaxSpeed: number; // 손 뗀 뒤 휘어짐 풀리는 속도 (1/s)
    flySpin: number; // 날아갈 때 회전량 (rad/s per (unit/s) at full lateral offset)
    flyMinSpeed: number; // 임계 넘겼는데 느리게 뗐을 때 최소 이탈 속도 (unit/s)
    flyDrag: number; // 비행 중 공기 저항 (1/s)
    slipDamping: number; // 미끄러지는 동안의 점성 감쇠비. 0 이면 카드가 손가락 주위를 진동한다.
    //   1 근처면 손가락에 딱 붙어 끌려온다. 낮출수록 헐렁하게 출렁인다.
  };
  // ------------------------------------------------------------------ AR·UR 등장 연출 (2026-09-16 신규)
  //
  // `haptic.rare*` / `sound.rare` 가 **예고편**(뒤에 뭔가 있다)이라면 이건 **타격**이다 —
  // 덮고 있던 카드가 비켜나 AR·UR 앞면이 실제로 드러나는 순간 한 번만 터진다.
  //
  // 규칙 세 가지 (손맛을 해치지 않기 위한 제약):
  //  1. **카드를 움직이지 않는다.** 드러난 카드는 곧 손가락이 잡을 카드다. 위치·크기·각도를 건드리면
  //     드래그가 연출에 끊긴다. 바뀌는 것은 **그 카드 앞면 셰이더의 색**뿐이다 (geometry.ts FRAG).
  //  2. DOM 오버레이 없음. three.js 셰이더로만 한다.
  //  3. 진행도는 sim 이 240Hz 로 적분해 RS 로 넘긴다 (CSS transition/animation 아님).
  //
  // 등급 차이는 `urScale` 하나로 낸다 — AR 이 기준값이고 UR 은 전부 이 배율이 곱해진다 (AR < UR).
  reveal: {
    on: boolean; // 연출 켜기. 끄면 소리·진동·빛 전부 안 난다
    minRank: number; // 이 등급 이상이 드러났을 때만 (0 커먼 / 1 레어 / 2 AR / 3 UR)
    dur: number; // 훑는 빛이 카드를 지나가는 시간 (s). 이 시간이 끝나면 연출도 끝난다
    bandWidth: number; // 훑는 빛의 폭 (UV). 좁을수록 날카로운 섬광
    band: number; // AR 기준 훑는 빛의 세기 (셰이더 가산값)
    flash: number; // AR 기준, 드러난 순간 카드 전면이 밝아지는 양 (셰이더 가산값)
    flashDecay: number; // 그 전면 밝아짐이 꺼지는 시정수 (s). dur 보다 훨씬 짧아야 "탁" 하고 끊긴다
    sound: number; // AR 기준 소리 세기 0..1 (sound.reveal 의 gain 에 곱해진다)
    haptic: number; // AR 기준 진동 길이 (ms)
    urScale: number; // UR 은 위 네 값에 이 배율. 1 이면 AR·UR 이 같아진다
  };
  // ------------------------------------------------------------------ 바인더
  // 개봉 화면의 손맛 값(cut/open/flip/swipe/world)과 **완전히 분리된 네임스페이스**다.
  // 바인더 크기는 world.objectScale 을 쓰지 않는다 — 화면 폭과 neighborPeek 에서 나온다 (§6.4).
  binder: {
    // 배치와 규모 (§8.1)
    minFaces: number; // 면 수의 절대 하한. 실제 면 수는 카드 수에서 유도한다 (cards.ts faceCount)
    spareFaces: number; // 마지막 카드 뒤에 붙는 빈 면
    neighborPeek: number; // 한쪽엔 펼침의 반대 면, 다른 쪽엔 커버가 걸치는 폭 (unit). 카드가 그만큼 작아진다
    fitH: number; // 시트 높이가 화면 세로에서 차지할 최대 비율. 가로로 넓은 창에서 시트가 잘리지 않게 하는 제약
    spreadAspect: number; // 화면 가로/세로 비가 이 값 이상이면 **양면 펼침 모드** (와이드). 아래면 포트레이트 (한 면만 중앙)
    spreadMargin: number; // 펼침 모드에서 좌우 바깥 여백 (unit). 폭 제약 = 2W + 2*여백 <= 화면 폭
    faceY: number; // 시트 중심 높이 (unit)
    cardInset: number; // 칸 안에서 카드를 줄이는 양 (칸 폭 비)
    badgeSize: number; // 수량 배지 지름 (칸 폭 비)
    // 카드 그림 텍스처 — **카드당 파일 하나** (2026-09-10 면 아틀라스 제거). 굽는 단계는 없어졌지만
    // GPU 상한은 그대로다: 현재 면 ±cellTexWindow 를 창으로 두고 나머지는 LRU 로 버린다.
    cellTexWidth: number; // 칸에 올릴 때 줄이는 폭 (px). 원본(768w)을 45장 그대로 올리면 GPU 가 감당 못 한다. 바꿔도 **이미 만든 텍스처는 안 바뀐다**
    cellTexWindow: number; // 현재 면 ±이만큼의 면에 있는(보유한) 카드 그림을 미리 받는다
    cellTexMax: number; // 칸 텍스처 LRU 상한 (장). 창 밖부터 버린다. 창 크기((2N+1)·9)보다 커야 의미가 있다
    detailTexMax: number; // 상세 보기 원본 LRU 상한 (장). 원본은 한 장이 칸 텍스처 수십 장 값이다
    glossBand: number; // 앞판 광택 밴드 세기. 0 = 코드 광택 없음
    glossBandWidth: number; // 밴드 폭 (UV)
    sheetThickness: number; // 더미 한 장당 z (unit)
    // 넘기기 공통 (§8.2)
    stepTravel: number; // 한 면을 넘기는 손가락 이동 거리 (unit). flip·pan 공통
    dragDeadzone: number; // 방향 결정 전 무시 거리 (unit)
    viewLag: number; // 카메라가 viewX(u) 를 따르는 1차 지연 (s). 0 = 정확히 묶임 (권장)
    edgeLimit: number; // 끝 면에서 따라오는 최대 진행 (u)
    edgeStiffness: number; // 끝 면 벽 스프링 (1/s^2)
    edgeSlide: number; // 끝 면에서 카메라가 밀리는 최대 거리 (unit)
    // flip — 링에 매달린 무거운 판 (§8.3)
    inertia: number; // 시트 관성 I
    gravity: number; // 능선 양쪽으로 넘어뜨리는 토크 G
    tilt: number; // 능선 위치 = pi/2 - tilt (rad)
    grabStiffness: number; // 손가락→시트 스프링 k (토크/rad)
    grabDamping: number; // 추종 감쇠비 zeta
    peelTorque: number; // 정지 저항. 한 번 넘으면 끝
    hingeFriction: number; // 링 점성 (토크/(rad/s))
    hingeDryFriction: number; // 링 건마찰 (토크). 0 이면 능선 근처에서 한없이 흔들린다
    landBounce: number; // 착지 되튐 (반발 계수)
    maxOmega: number; // 각속도 상한 (rad/s, 안전장치)
    // pan — 바인더가 책상 위에서 밀린다 (§8.4)
    panSnap: number; // 놓은 뒤 가까운 면 중심으로 붙는 스프링 응답 (1/s)
    panDamping: number; // 스냅 감쇠비
    panFlick: number; // 이 진행 속도(1/s) 이상으로 놓으면 절반 못 갔어도 넘어간다
    panSoundScale: number; // pan 중 쓸림음의 음량 배율 (flip 대비)
    // 수납 연출 (§8.5)
    //
    // **2026-09-16 에 모델이 바뀌었다 (사용자 요청).** 예전에는 카드를 전부 동시에 띄우고
    // 현재 면에 없는 카드는 등(spine) 으로 빨아들여 없앴다. 지금은 **슬롯 순서로 한 장씩** 처리하고,
    // 그 카드의 면이 아니면 **바인더를 그 면까지 실제로 넘긴다** (기존 넘기기 물리를 그대로 쓴다 —
    // 가상의 손가락이 targetU 를 stowTurnTime 에 걸쳐 0→1 로 끌 뿐이다). 그래서 카드 간 겹침이
    // 없어졌고 `stowStagger` 도 없앴다.
    stowSpeed: number; // 카드가 칸으로 가는 스프링 응답 (1/s). zeta 1.0 고정
    stowDelay: number; // 바인더가 올라온 뒤 첫 카드까지 (s)
    stowRiseSpeed: number; // 바인더가 화면 아래서 올라오는 스프링 응답 (1/s)
    stowTurnTime: number; // 가상 손가락이 한 면을 넘기는 데 쓰는 시간 (s). 손으로 넘길 때와 같은 물리다
    stowTurnPause: number; // 목표 면에 도착한 뒤 카드가 내려앉기까지 (s)
    stowGap: number; // 한 장이 착지한 뒤 다음 카드까지 (s)
    stowDuration: number; // **단계 하나**의 강제 종료 시간 (s, 안전장치). 면 넘기기 하나 / 카드 한 장의 낙하에 각각 적용된다.
    //   전체 시간이 아니다 — 목표 면이 멀면 넘기기가 여러 번 일어나므로 총 시간은 카드 수와 면 거리에 따라 늘어난다.
    stowStartDist: number; // 카드가 출발하는 화면 밖 거리 (unit)
    // 상세 보기 (§8.6)
    tapMaxMs: number; // 이 시간 안에 dragDeadzone 을 안 벗어나고 떼면 탭
    detailWidthFrac: number; // 확대된 카드 폭 (화면 폭 비)
    detailFitH: number; // 확대된 카드 높이의 상한 (화면 세로 비). 가로로 넓은 화면에서 카드가 위아래로 넘치는 것을 막는다
    detailY: number; // 확대된 카드 중심 높이 (unit)
    detailLift: number; // 확대 상태의 z (unit)
    detailPull: number; // 1단계에서 슬리브 위로 빠져나오는 거리 (칸 높이 비)
    detailSpeed: number; // 열림·닫힘·이웃 슬라이드 스프링 응답 (1/s)
    detailDamping: number; // 감쇠비. 1 미만이면 살짝 지나쳤다 돌아온다
    detailDim: number; // 뒤 바인더를 어둡게 하는 정도 0..1
    detailUnownedDim: number; // 미보유 카드(뒷면)를 어둡게 하는 정도 0..1
    detailCloseDist: number; // 아래로 이만큼(unit) 끌고 놓으면 닫힌다
    detailNavDist: number; // 옆으로 이만큼(unit) 끌고 놓으면 이웃 칸
    detailFlick: number; // 거리 미달이어도 이 속도(unit/s)로 놓으면 닫힘/이웃
    detailNavEdge: number; // 면의 첫/끝 칸에서 더 끌 때 따라오는 비율
  };
  // ------------------------------------------------------------------ 경제 (재화)
  /**
   * 타이틀 화면. 키아트 한 장 + TAP TO START 버튼뿐이다.
   * 아트 규격과 색 토큰은 `docs/TITLE_SCREEN.md` 가 정본 (전부 키아트에서 실측한 값).
   *
   * **버튼의 모든 움직임은 매 프레임 계산한다** — CSS transition/animation 금지(절대 규칙).
   * 숨쉬기·눌림·페이드아웃 전부 main.ts 의 stepTitle() 이 적분해서 style 에 직접 넣는다.
   */
  title: {
    btnBottomFrac: number; // 버튼 중심의 화면 아래에서의 위치 (0..1). 키아트 하단 22% 빈 띠 안
    btnPressDrop: number; // 눌렀을 때 내려가는 거리 (px)
    btnPressDim: number; // 눌렀을 때 밝기 배율 (1 = 그대로)
    pulsePeriod: number; // 숨쉬기 한 주기 (s). 0 이면 안 쉼
    pulseAmp: number; // 숨쉬기 크기 변화폭 (0.03 = ±3%)
    fadeDur: number; // 누른 뒤 화면이 걷히는 시간 (s)
  };

  /**
   * 타이틀 BGM — **실시간 합성 루프** (src/bgm.ts). 오디오 파일이 아니다(절대 규칙).
   * 8마디 16분 격자 시퀀서라 루프 이음새가 구조적으로 없다.
   * gain 은 sound.master 아래에 걸린다 — 음소거(M)가 BGM 에도 먹는다.
   */
  bgm: {
    on: boolean; // 끄면 스텝 예약을 멈춘다 (이미 예약된 음은 자연히 끝난다)
    gain: number; // BGM 버스 음량 (효과음 대비. 효과음을 덮지 않게 낮게 잡는다)
    bpm: number; // 템포
    lookahead: number; // 몇 초 앞까지 미리 예약하나. 크면 안전하고 파라미터 반응이 늦다
    fadeIn: number; // 시작 페이드 (s)
    fadeOut: number; // 정지 페이드 (s). 기본값은 title.fadeDur 과 맞춰 둔다
    lead: number; // 주선율 음량
    leadOct: number; // 주선율 한 옥타브 위 겹침 비율 (0 이면 안 겹침)
    leadCut: number; // 주선율 로패스 (Hz)
    chop: number; // 뒷박 화음 스탭 음량 — 발랄함의 대부분이 여기서 온다
    chopCut: number; // 스탭 로패스 (Hz)
    bass: number; // 베이스 음량
    bassCut: number; // 베이스 로패스 (Hz)
    bell: number; // 마디 첫박 반짝임 음량
    drum: number; // 킥/스네어/하이햇 음량
  };

  // 전부 **달러**다. 내부 계산은 센트 정수로 한다 — 0.05 를 계속 더하면 부동소수 오차가 쌓인다 (src/econ.ts).
  //
  // **무한 증식 경고.** 팩 하나를 다 팔았을 때의 기댓값은
  //     CARD_COUNT × Σ(등급 확률 × 그 등급 판매가)
  // 이고, 이것이 packPrice 를 넘으면 "사서 전부 팔기" 만으로 돈이 무한히 늘어난다.
  // 지금 값: 5 × $0.4604 = $2.3022 < $4.49 (회수율 51.3%, 팩당 -$2.19 싱크).
  //   ※ 2026-09-16 에 CARD_COUNT 가 8 → 5 로 줄어 기댓값이 $3.6960 → $2.3022 로 떨어졌다.
  //     회수율도 82.3% → 51.3%. **가격은 사용자 결정 사항이라 packPrice 를 건드리지 않았다.**
  // **여기 값이나 P.draw 의 확률을 바꾸면 반드시 `node tools/econ-stats.mjs` 로 다시 확인하라.**
  econ: {
    startMoney: number; // 저장 데이터가 없을 때의 시작 소지금 ($)
    packPrice: number; // 팩 하나 값 ($)
    sellCommon: number; // 판매가 ($) — 등급 인덱스는 CARD_KINDS 와 같다
    sellRare: number;
    sellAr: number;
    sellUr: number;
    adReward: number; // 광고 보상 ($). 지금은 SDK 없이 즉시 지급 (임시)
    adLongReward: number; // 긴 광고 보상 ($)
    adLongWait: number; // 긴 광고 대기 (s). 진짜 광고 SDK 가 들어올 자리
    // ---- 차감/획득 텍스트 (`-$4.49`). 팩을 뜯는 순간 팩 중앙에서 떠오르며 사라진다.
    // **CSS animation 이 아니다** — sim 이 240Hz 로 진행도를 적분하고 render 가 보간해 그린다.
    floatDur: number; // 지속 (s)
    floatRise: number; // 떠오르는 거리 (unit). 감속하며 오른다
    floatFade: number; // 페이드가 시작되는 진행도 (0..1). 그 전까지는 불투명
    floatSize: number; // 글자 높이 (unit)
    floatY: number; // 뜨는 높이 (팩 로컬 y, unit). 0 = 팩 중앙
  };
  input: {
    fastTau: number; // 빠른 속도 추정 시정수 (s) — 소리/진동
    slowTau: number; // 느린 속도 추정 시정수 (s) — 시각 변형
    holdMs: number; // 이 시간(ms) 동안 샘플이 없으면 정지로 간주
    releaseWindowMs: number; // 관성 속도 계산에 쓰는 마지막 샘플 창 (ms)
    releaseStaleMs: number; // 마지막 샘플이 이보다 오래됐으면 관성 0
    useCoalesced: boolean; // 프레임 사이 삼켜진 좌표 복원 사용
  };
  sound: {
    master: number;
    cut: { gain: number; speedForMax: number; freqMin: number; freqMax: number; q: number; attack: number; release: number };
    crackle: { gain: number; rateMin: number; rateMax: number; durMin: number; durMax: number; hpf: number };
    penetrate: { gain: number; freq: number; dur: number };
    complete: { gain: number; freq: number; dur: number };
    friction: { gain: number; speedForMax: number; freqMin: number; freqMax: number; q: number; attack: number; release: number };
    click: { gain: number; dur: number; hpf: number };
    thud: { gain: number; freq: number; dur: number; noise: number };
    touch: { gain: number; dur: number };
    // 바인더 시트가 공기를 가르는 낮은 쓸림 (지속음). friction 과 같은 구조, 더 낮고 꼬리가 길다.
    page: { gain: number; speedForMax: number; freqMin: number; freqMax: number; q: number; attack: number; release: number };
    // 시트가 더미에 닿는 순간 (원샷). thud 와 같은 구조, 더 낮고 짧게.
    pageSlap: { gain: number; freq: number; dur: number; noise: number };
    // 레어 예고음 — 드러난 비율에 따라 고조되는 리저(riser). 클립 재생 아님, 실시간 합성.
    // 노이즈 밴드가 위로 쓸려 올라가고(hiss) 저음 사인이 같이 올라온다(tone).
    // 맥동(pulse)은 노이즈 대역폭을 흔들어 심장박동 같은 흔들림을 준다.
    rare: {
      gain: number; // 노이즈 성분 최대 음량
      freqMin: number; // 시작 밴드패스 중심 (Hz)
      freqMax: number; // 끝 밴드패스 중심 (Hz)
      qMin: number; // 시작 Q (넓게)
      qMax: number; // 끝 Q (좁게 = 울리는 소리)
      toneGain: number; // 사인 성분 최대 음량
      toneMin: number; // 사인 시작 주파수 (Hz)
      toneMax: number; // 사인 끝 주파수 (Hz)
      pulseRate: number; // 맥동 빈도 (Hz). 0 이면 맥동 없음
      pulseDepth: number; // 맥동 깊이 (0..1)
      attack: number; // 커지는 쪽 시정수 (s)
      release: number; // 꺼지는 쪽 시정수 (s)
      curve: number; // 고조 곡선 지수. 1=선형, >1 이면 끝에서 가파르게
    };
    // AR·UR 이 드러나는 순간의 타격음 (원샷). **클립 재생이 아니다** — 종(사인 배음 셋)과
    // 반짝임(노이즈 하이패스)을 그 자리에서 합성한다. 노이즈는 다른 재질음과 같은 버퍼에서 갈라진다.
    reveal: {
      gain: number; // 종 기본 음량 (P.reveal.sound 가 여기 곱해진다)
      freq: number; // 종 기본 주파수 (Hz)
      dur: number; // 종이 잦아드는 시간 (s)
      glide: number; // 감쇠 동안의 주파수 상승 배율. 1 = 안 올라감
      shimmer: number; // 반짝임(노이즈) 음량, 종 대비 비율
      shimmerDur: number; // 반짝임 길이 (s)
      hpf: number; // 반짝임 하이패스 컷오프 (Hz)
    };
  };
  haptic: {
    penetrate: number; // ms
    complete: number;
    slip: number;
    land: number;
    // 자르는 동안의 연속 진동. 지시서 §8 은 네 지점 펄스만 쓰라고 했으나 요청으로 추가했다.
    // 웹 진동은 세기 조절이 없어서, 짧은 펄스를 속도에 비례한 빈도로 쏘는 것으로만 흉내낼 수 있다.
    // 펄스 길이 ≥ 간격 이면 모터가 끊기지 않아 "계속 오는" 진동이 된다.
    cutContinuous: boolean;
    cutPulse: number; // 펄스 하나의 길이 (ms)
    cutRateMin: number; // 칼날이 막 움직일 때의 빈도 (/s)
    cutRateMax: number; // 최고 속도에서의 빈도 (/s)
    cutSpeedForMax: number; // 이 칼날 속도에서 cutRateMax 에 닿는다 (unit/s)
    cutMinSpeed: number; // 이 속도 미만이면 아예 안 쏜다 (unit/s).
    //   소리는 음량이 0 으로 줄어 저절로 사라지지만, 진동은 세기 조절이 없어 문턱이 필요하다.
    //   이게 없으면 손을 멈춘 뒤 칼날이 기어가는 동안에도 계속 울린다.

    // --- 레어 예고: 뒤에 높은 등급이 있으면 드래그를 시작하는 순간부터 최강 진동.
    // 지시서 §14 는 "등급별 연출 차등"을 비범위로 두었으나 요청으로 추가했다. rareOn 으로 끈다.
    // 웹 진동은 세기 조절이 없으므로 "최강" = 끊김 없는 연속 진동 (1/rate <= rarePulse).
    // 고조(크레셴도)는 소리가 맡는다 — sound.rare 참고. 소리는 세기가 있고,
    // 진동이 아예 없는 iOS 에도 전달되기 때문이다.
    rareOn: boolean;
    rareMinRank: number; // 이 등급 이상이 뒤에 있을 때만 (0 커먼 / 1 레어 / 2 AR / 3 UR)
    rareFrom: number; // 드러난 비율이 이 값을 넘으면 시작. 0 = 드래그하는 순간부터
    rareTo: number; // 이 비율에 닿으면 멈춘다 (1 = 다 보이면 무진동·무음)
    rarePulse: number; // 펄스 길이 (ms)
    rareRate: number; // 초당 펄스 수. 간격(1/rate)이 펄스보다 짧아야 모터가 안 끊긴다

    // --- iOS 네이티브 전용 (@capacitor/haptics). 웹/Android 경로는 이 값들을 안 본다.
    // iOS 는 진동 **길이**를 못 준다. Light/Medium/Heavy 세 단계뿐이라 위의 ms 값을
    // 아래 문턱으로 접어서 보낸다 (src/haptics.ts).
    // **아직 실기기에서 맞춰 보지 않은 출발점 값이다.** 폰에서 만져 보고 조정할 것.
    iosLightMax: number; // 이 ms 이하 → LIGHT
    iosMediumMax: number; // 이 ms 이하 → MEDIUM, 초과 → HEAVY
    // 연속 진동(cutContinuous)이 초당 22~70회를 쏘는데 iOS 는 매번 네이티브 브리지를
    // 타는 비동기 호출이라 그대로 보내면 큐가 밀려 손을 뗀 뒤에도 진동이 남는다.
    // 이 간격보다 자주 온 호출은 버린다. 0 이면 솎지 않는다.
    iosMinIntervalMs: number;
  };
}

export const DEFAULT_PARAMS: Params = {
  world: {
    objectScale: 1.2,
  },
  draw: {
    weightCommon: 70,
    weightRare: 22,
    weightAr: 6,
    // **0.6 — 2 에서 낮췄다.** UR 은 5 장뿐이라 등급 확률 2% 를 5 로 나누면 장당 0.40% 가 되어
    // AR 1 장(25종, 0.24%)보다 오히려 흔했다. 가장 귀한 등급이 그 아래보다 잘 나오면 안 된다.
    // 0.6 이면 장당 0.122% 로 AR 의 절반이 된다 (특정 UR 1장에 약 103팩).
    weightUr: 0.6,
    rareLateSlots: 2,
  },
  cut: {
    perfFrac: 0.12,
    bandHalf: 0.9,
    bladeStiffness: 26,
    bladeDamping: 0.9,
    penetrationResistance: 3.2,
    penetrationDepth: 0.22,
    reentryFactor: 0.45,
    baseResistance: 1.0,
    stickRatio: 1.35,
    jitterAmount: 0.45,
    jitterFreq: 6,
    velEffect: 0.06,
    lineJitter: 0.045,
    lineJitterFreq: 9,
    gapAmount: 0.16,
    gapRamp: 1.2,
    gapVelEffect: 0.02,
    bulgeAmount: 0.06,
    bulgeRadius: 0.35,
    bulgeLead: 0.12,
    directionDeadzone: 0.12,
  },
  open: {
    topFlySpeed: 15,
    topSpin: 4.5,
    topGravity: 10,
    topDriftFactor: 0.25,
    bodySinkDistance: 14.0,
    bodySinkSpeed: 7,
    bodySinkDamping: 1.05,
    cardDelay: 0.11,
    cardStagger: 0.018,
    cardRiseSpeed: 11,
    cardRiseDamping: 0.78,
    cardStartDepth: 0.8,
    stackY: 0.4,
    spreadPos: 0,
    spreadAngle: 0,
    timeScale: 1.0,
    cardTexWidth: 640, // 640×895 RGBA+밉맵 ≈ 3.1MB. 5장 ≈ 16MB
  },
  flip: {
    duration: 0.3,
    overshoot: 0.08,
    tapInfluence: 0.12,
    rollReturnSpeed: 9,
  },
  swipe: {
    stickResistance: 1.0,
    slipResistance: 0.55,
    bendStiffness: 32,
    bendVisual: 0,
    bendLimit: 0.25,
    bendLimitFrac: 0.35,
    mass: 0.012,
    threshold: 2.2,
    flickSpeed: 22,
    returnSpeed: 22,
    returnBounce: 0.42,
    bendRelaxSpeed: 40,
    flySpin: 0.25,
    flyMinSpeed: 9,
    flyDrag: 0.4,
    slipDamping: 0.7,
  },
  // **실기기 판정을 거치지 않았다.** 헤드리스로는 빛도 소리도 못 본다 — 손으로 보고 조정할 것.
  reveal: {
    on: true,
    minRank: 2,
    dur: 0.5,
    bandWidth: 0.16,
    band: 0.55,
    flash: 0.22,
    flashDecay: 0.07,
    sound: 0.62,
    haptic: 18,
    urScale: 1.6,
  },
  // 전부 [제안]이고 **실기기 판정을 거치지 않았다** (BINDER_DESIGN §8).
  binder: {
    minFaces: 10,
    spareFaces: 4,
    neighborPeek: 0.5,
    fitH: 0.92,
    spreadAspect: 1.2,
    spreadMargin: 0.5,
    faceY: 0.0,
    // 칸과 카드가 이제 같은 비율(1.3984)이라 가로·세로가 함께 96% 로 채워진다.
    // 음수로 밀어 넣을 필요가 없어졌다 — 칸 비율이 카드와 어긋났을 때의 임시방편이었다.
    cardInset: 0.02,
    badgeSize: 0.28,
    cellTexWidth: 320, // 칸 표시 폭(≈290px)보다 조금 크게. 320×448 RGBA+밉맵 ≈ 0.8MB
    cellTexWindow: 2, // 5면 × 9칸 = 최대 45장
    cellTexMax: 64, // 창 45장 + 여유. 64장 ≈ 49MB
    detailTexMax: 6,
    glossBand: 0.35,
    glossBandWidth: 0.12,
    sheetThickness: 0.01,
    stepTravel: 6.0,
    dragDeadzone: 0.12,
    viewLag: 0,
    edgeLimit: 0.12,
    edgeStiffness: 400,
    edgeSlide: 0.8,
    inertia: 0.05,
    gravity: 1.0,
    tilt: 0,
    grabStiffness: 40,
    grabDamping: 0.8,
    peelTorque: 0.6,
    hingeFriction: 0.4,
    hingeDryFriction: 0.15,
    landBounce: 0.12,
    maxOmega: 40,
    panSnap: 14,
    panDamping: 0.9,
    panFlick: 1.2,
    panSoundScale: 0.5,
    stowSpeed: 13.5,      // 낙하 50% 빠르게 (9 → 13.5)
    stowDelay: 0.167,     // 첫 장까지 대기도 50% 빠르게 (0.25 → 0.167)
    stowRiseSpeed: 7,
    stowTurnTime: 0.107, // 3배 빠르게 (0.32 → 0.107, 2026-09-16 사용자 요청)
    stowTurnPause: 0.04,  // 넘김과 같은 비율로 (0.12 → 0.04)
    stowGap: 0.12,        // 장 사이 간격도 50% 빠르게 (0.18 → 0.12)
    stowDuration: 2.5,
    stowStartDist: 4.5,   // 칸 위 높이. 옛 값 12 는 화면 밖 대각선 비행 기준이었다
    tapMaxMs: 250,
    detailWidthFrac: 0.82,
    detailFitH: 0.86,
    detailY: 0.4,
    detailLift: 0.6,
    detailPull: 0.35,
    detailSpeed: 14,
    detailDamping: 0.85,
    detailDim: 0.55,
    detailUnownedDim: 0.35,
    detailCloseDist: 2.0,
    detailNavDist: 2.5,
    detailFlick: 14,
    detailNavEdge: 0.3,
  },
  // 사용자 확정 값. 근거와 검증은 위 인터페이스 주석 + docs/PIVOT.md "경제" 참고.
  title: {
    btnBottomFrac: 0.115,
    btnPressDrop: 6,
    btnPressDim: 0.92,
    pulsePeriod: 2.4,
    pulseAmp: 0.028,
    fadeDur: 0.32,
  },

  bgm: {
    on: true,
    gain: 0.34,
    bpm: 128,
    lookahead: 0.18,
    fadeIn: 0.8,
    fadeOut: 0.32, // title.fadeDur 과 같다 — 화면이 걷히는 동안 소리도 같이 걷힌다
    lead: 0.1,
    leadOct: 0.35,
    leadCut: 3200,
    chop: 0.052,
    chopCut: 2200,
    bass: 0.16,
    bassCut: 900,
    bell: 0.045,
    drum: 0.2,
  },

  econ: {
    // **$50 — $20 에서 올렸다 (2026-09-21 사용자 결정).** 초기 지급은 $50 + 미개봉 팩 10개다.
    // 이것과 하루 무료 8팩(무료 5 + 광고 3)이 "한 달에 225종 컴플리트" 곡선의 전제다.
    // 근거·시뮬레이션은 docs/PIVOT.md "수급 — 정해졌다 (2026-09-21)".
    startMoney: 50.0,
    packPrice: 4.49,
    sellCommon: 0.05,
    sellRare: 0.35,
    sellAr: 2.5,
    // **$32 — $10 에서 올렸다.** UR 가중치를 2 → 0.6 으로 낮추면서(5장뿐이라 AR 보다 흔했다)
    // 팩 기댓값이 $3.70 → $2.61 로 떨어져 시작금 $20 이 4팩만에 말랐다.
    // 희귀해진 만큼 값을 올려 회수율을 원래대로 되돌린다 (82.3%).
    sellUr: 32.0,
    adReward: 15.0,
    adLongReward: 20.0,
    adLongWait: 3.0,
    floatDur: 0.9,
    floatRise: 1.8,
    floatFade: 0.45,
    floatSize: 1.15,
    floatY: 4.8,
  },
  input: {
    fastTau: 0.012,
    slowTau: 0.045,
    holdMs: 28,
    releaseWindowMs: 45,
    releaseStaleMs: 70,
    useCoalesced: true,
  },
  sound: {
    master: 0.8,
    cut: { gain: 0.55, speedForMax: 14, freqMin: 900, freqMax: 3800, q: 1.1, attack: 0.006, release: 0.012 },
    crackle: { gain: 0.6, rateMin: 8, rateMax: 140, durMin: 0.0015, durMax: 0.005, hpf: 2600 },
    penetrate: { gain: 0.7, freq: 1500, dur: 0.035 },
    complete: { gain: 0.6, freq: 700, dur: 0.06 },
    friction: { gain: 0.5, speedForMax: 12, freqMin: 1400, freqMax: 5200, q: 1.6, attack: 0.004, release: 0.01 },
    click: { gain: 0.5, dur: 0.004, hpf: 3500 },
    thud: { gain: 0.5, freq: 95, dur: 0.09, noise: 0.5 },
    touch: { gain: 0.08, dur: 0.006 },
    page: { gain: 0.4, speedForMax: 25, freqMin: 300, freqMax: 1600, q: 0.9, attack: 0.008, release: 0.03 },
    pageSlap: { gain: 0.5, freq: 70, dur: 0.11, noise: 0.7 },
    rare: {
      gain: 0.3,
      freqMin: 240,
      freqMax: 2900,
      qMin: 1.2,
      qMax: 7,
      toneGain: 0.14,
      toneMin: 82,
      toneMax: 330,
      pulseRate: 5.5,
      pulseDepth: 0.45,
      attack: 0.04,
      release: 0.08,
      curve: 1.8,
    },
    reveal: { gain: 0.42, freq: 784, dur: 1.1, glide: 1.06, shimmer: 0.5, shimmerDur: 0.13, hpf: 4200 },
  },
  haptic: {
    penetrate: 8,
    complete: 14,
    slip: 5,
    land: 10,
    cutContinuous: true,
    cutPulse: 16,
    cutRateMin: 22,
    cutRateMax: 70,
    cutSpeedForMax: 14,
    cutMinSpeed: 0.4,
    rareOn: true,
    rareMinRank: 2,
    rareFrom: 0,
    rareTo: 1.0,
    rarePulse: 26,
    rareRate: 55,
    // iOS: penetrate 8 / slip 5 → LIGHT, land 10 / complete 14 / cutPulse 16 → MEDIUM,
    // rarePulse 26 → HEAVY 가 되도록 잡은 출발점. 실기기에서 조정할 것.
    iosLightMax: 9,
    iosMediumMax: 20,
    // 초당 최대 50회. cutRateMax 70 을 그대로 흘리면 브리지가 밀린다.
    iosMinIntervalMs: 20,
  },
};

// 튜닝 패널 슬라이더 범위. 키는 "group.key" 또는 "sound.layer.key".
export const RANGES: Record<string, [number, number, number]> = {
  'world.objectScale': [0.5, 2, 0.01],
  'draw.weightCommon': [0, 100, 0.5],
  'draw.weightRare': [0, 100, 0.5],
  'draw.weightAr': [0, 100, 0.5],
  'draw.weightUr': [0, 100, 0.1], // 스텝 0.1 — 기본값 0.6 이 0.5 스텝에 안 맞았다
  // AR·UR 이 나올 수 있는 뒤쪽 자리 수. 바꾸면 자리별 확률이 재분배된다 (기대 장수는 유지).
  'draw.rareLateSlots': [0, CARD_COUNT, 1],
  // 경제 — 슬라이더로 바꿔도 저장된 소지금은 안 변한다 (startMoney 는 새 저장소에만 쓰인다).
  'title.btnBottomFrac': [0.04, 0.3, 0.005],
  'title.btnPressDrop': [0, 20, 1],
  'title.btnPressDim': [0.5, 1, 0.01],
  'title.pulsePeriod': [0, 6, 0.1],
  'title.pulseAmp': [0, 0.15, 0.005],
  'title.fadeDur': [0, 1.5, 0.02],
  // BGM (src/bgm.ts). on 은 boolean 이라 범위가 없다 (패널이 체크박스로 만든다).
  'bgm.gain': [0, 1, 0.01],
  'bgm.bpm': [60, 200, 1],
  'bgm.lookahead': [0.05, 0.5, 0.01],
  'bgm.fadeIn': [0, 3, 0.05],
  'bgm.fadeOut': [0, 3, 0.05],
  'bgm.lead': [0, 0.4, 0.005],
  'bgm.leadOct': [0, 1, 0.05],
  'bgm.leadCut': [400, 12000, 100],
  'bgm.chop': [0, 0.3, 0.002],
  'bgm.chopCut': [400, 12000, 100],
  'bgm.bass': [0, 0.4, 0.005],
  'bgm.bassCut': [150, 4000, 50],
  'bgm.bell': [0, 0.2, 0.005],
  'bgm.drum': [0, 0.6, 0.01],
  // 가격·확률을 만졌으면 tools/econ-stats.mjs 로 무한 증식을 확인할 것.
  'econ.startMoney': [0, 1000, 1],
  'econ.packPrice': [0.5, 20, 0.01],
  'econ.sellCommon': [0, 5, 0.01],
  'econ.sellRare': [0, 10, 0.01],
  'econ.sellAr': [0, 30, 0.05],
  'econ.sellUr': [0, 100, 0.1],
  'econ.adReward': [0, 100, 0.5],
  'econ.adLongReward': [0, 100, 0.5],
  'econ.adLongWait': [0, 30, 0.5],
  'econ.floatDur': [0.2, 3, 0.05],
  'econ.floatRise': [0, 6, 0.1],
  'econ.floatFade': [0, 1, 0.05],
  'econ.floatSize': [0.3, 3, 0.05],
  'econ.floatY': [-5, 5, 0.1],
  'cut.perfFrac': [0.05, 0.3, 0.001],
  'cut.bandHalf': [0.3, 2, 0.05],
  'cut.bladeStiffness': [4, 80, 1],
  'cut.bladeDamping': [0.1, 4, 0.05],
  'cut.penetrationResistance': [0, 10, 0.1],
  'cut.penetrationDepth': [0.02, 1, 0.01],
  'cut.reentryFactor': [0, 1, 0.05],
  'cut.baseResistance': [0, 4, 0.05],
  'cut.stickRatio': [1, 2.5, 0.05],
  'cut.jitterAmount': [0, 1, 0.01],
  'cut.jitterFreq': [0.5, 30, 0.5],
  'cut.velEffect': [0, 0.3, 0.005],
  'cut.lineJitter': [0, 0.2, 0.005],
  'cut.lineJitterFreq': [1, 30, 0.5],
  'cut.gapAmount': [0, 0.6, 0.01],
  'cut.gapRamp': [0.1, 4, 0.1],
  'cut.gapVelEffect': [0, 0.1, 0.005],
  'cut.bulgeAmount': [0, 0.3, 0.005],
  'cut.bulgeRadius': [0.1, 1.2, 0.05],
  'cut.bulgeLead': [0, 0.5, 0.01],
  'cut.directionDeadzone': [0, 0.5, 0.01],
  'open.topFlySpeed': [3, 40, 0.5],
  'open.topSpin': [0, 15, 0.1],
  'open.topGravity': [0, 40, 0.5],
  'open.topDriftFactor': [0, 1, 0.05],
  'open.bodySinkDistance': [2, 20, 0.1],
  'open.bodySinkSpeed': [1, 30, 0.5],
  'open.bodySinkDamping': [0.3, 2, 0.05],
  'open.cardDelay': [0, 0.5, 0.01],
  'open.cardStagger': [0, 0.08, 0.002],
  'open.cardRiseSpeed': [2, 30, 0.5],
  'open.cardRiseDamping': [0.3, 1.5, 0.02],
  'open.cardStartDepth': [0, 5, 0.1],
  'open.stackY': [-2, 3, 0.1],
  'open.spreadPos': [0, 0.3, 0.005],
  'open.spreadAngle': [0, 0.1, 0.002],
  'open.timeScale': [0.3, 3, 0.05],
  'open.cardTexWidth': [128, 1024, 32],
  'flip.duration': [0.1, 1.2, 0.01],
  'flip.overshoot': [0, 0.5, 0.01],
  'flip.tapInfluence': [0, 0.4, 0.01],
  'flip.rollReturnSpeed': [1, 30, 0.5],
  'swipe.stickResistance': [0, 4, 0.05],
  'swipe.slipResistance': [0, 4, 0.05],
  'swipe.bendStiffness': [1, 40, 0.5],
  'swipe.bendVisual': [0, 1, 0.05],
  'swipe.bendLimit': [0.05, 2, 0.01],
  'swipe.bendLimitFrac': [0.05, 0.6, 0.01],
  'swipe.mass': [0.002, 0.1, 0.001],
  'swipe.threshold': [0.3, 6, 0.1],
  'swipe.flickSpeed': [5, 60, 1],
  'swipe.returnSpeed': [4, 60, 1],
  'swipe.returnBounce': [0.1, 1.2, 0.02],
  'swipe.bendRelaxSpeed': [5, 120, 1],
  'swipe.flySpin': [0, 3, 0.05],
  'swipe.flyMinSpeed': [0, 30, 0.5],
  'swipe.flyDrag': [0, 3, 0.05],
  'swipe.slipDamping': [0, 2, 0.05],
  // AR·UR 등장 연출. on 은 boolean 이라 범위가 없다 (패널이 체크박스로 만든다).
  'reveal.minRank': [0, 3, 1],
  'reveal.dur': [0.1, 2, 0.01],
  'reveal.bandWidth': [0.03, 0.6, 0.01],
  'reveal.band': [0, 2, 0.01],
  'reveal.flash': [0, 1.5, 0.01],
  'reveal.flashDecay': [0.01, 0.5, 0.005],
  'reveal.sound': [0, 1, 0.01],
  'reveal.haptic': [0, 80, 1],
  'reveal.urScale': [1, 3, 0.05],
  'binder.minFaces': [2, 400, 2],
  'binder.spareFaces': [0, 40, 2],
  'binder.neighborPeek': [0, 2, 0.05],
  'binder.fitH': [0.5, 1, 0.01],
  'binder.spreadAspect': [1, 3, 0.05],
  'binder.spreadMargin': [0, 4, 0.05],
  'binder.faceY': [-4, 4, 0.05],
  'binder.cardInset': [-0.05, 0.25, 0.005], // 음수 허용 — 칸보다 크게 넣을 수 있어야 한다
  'binder.badgeSize': [0.1, 0.6, 0.01],
  'binder.cellTexWidth': [96, 768, 16],
  'binder.cellTexWindow': [0, 6, 1],
  'binder.cellTexMax': [9, 240, 1],
  'binder.detailTexMax': [1, 24, 1],
  'binder.glossBand': [0, 1, 0.01],
  'binder.glossBandWidth': [0.02, 0.5, 0.01],
  'binder.sheetThickness': [0, 0.3, 0.005],
  'binder.stepTravel': [2, 12, 0.1],
  'binder.dragDeadzone': [0, 0.5, 0.01],
  'binder.viewLag': [0, 0.15, 0.005],
  'binder.edgeLimit': [0.02, 0.5, 0.01],
  'binder.edgeStiffness': [50, 2000, 10],
  'binder.edgeSlide': [0, 3, 0.05],
  'binder.inertia': [0.005, 0.5, 0.005],
  'binder.gravity': [0, 5, 0.05],
  'binder.tilt': [-0.6, 0.6, 0.01],
  'binder.grabStiffness': [2, 150, 1],
  'binder.grabDamping': [0, 2, 0.05],
  'binder.peelTorque': [0, 4, 0.05],
  'binder.hingeFriction': [0, 4, 0.05],
  'binder.hingeDryFriction': [0, 2, 0.01],
  'binder.landBounce': [0, 0.6, 0.01],
  'binder.maxOmega': [5, 120, 1],
  'binder.panSnap': [3, 40, 0.5],
  'binder.panDamping': [0.3, 1.5, 0.05],
  'binder.panFlick': [0, 6, 0.05],
  'binder.panSoundScale': [0, 1, 0.05],
  'binder.stowSpeed': [2, 30, 0.5],
  'binder.stowDelay': [0, 1, 0.01],
  'binder.stowRiseSpeed': [2, 30, 0.5],
  'binder.stowTurnTime': [0.08, 1.5, 0.01],
  'binder.stowTurnPause': [0, 1, 0.01],
  'binder.stowGap': [0, 1, 0.01],
  'binder.stowDuration': [0.5, 6, 0.1],
  'binder.stowStartDist': [4, 24, 0.5],
  'binder.tapMaxMs': [80, 600, 10],
  'binder.detailWidthFrac': [0.4, 1, 0.01],
  'binder.detailFitH': [0.3, 1, 0.01],
  'binder.detailY': [-4, 4, 0.05],
  'binder.detailLift': [0.1, 3, 0.05],
  'binder.detailPull': [0, 1, 0.01],
  'binder.detailSpeed': [3, 40, 0.5],
  'binder.detailDamping': [0.3, 1.5, 0.05],
  'binder.detailDim': [0, 0.95, 0.01],
  'binder.detailUnownedDim': [0, 0.9, 0.01],
  'binder.detailCloseDist': [0.5, 6, 0.1],
  'binder.detailNavDist': [0.5, 6, 0.1],
  'binder.detailFlick': [0, 40, 0.5],
  'binder.detailNavEdge': [0, 1, 0.05],
  'input.fastTau': [0.002, 0.08, 0.001],
  'input.slowTau': [0.01, 0.25, 0.005],
  'input.holdMs': [8, 80, 1],
  'input.releaseWindowMs': [10, 150, 5],
  'input.releaseStaleMs': [20, 200, 5],
  'sound.master': [0, 1, 0.01],
  'sound.cut.gain': [0, 1, 0.01],
  'sound.cut.speedForMax': [2, 40, 0.5],
  'sound.cut.freqMin': [100, 8000, 10],
  'sound.cut.freqMax': [100, 12000, 10],
  'sound.cut.q': [0.3, 8, 0.1],
  'sound.cut.attack': [0.001, 0.05, 0.001],
  'sound.cut.release': [0.001, 0.1, 0.001],
  'sound.crackle.gain': [0, 1, 0.01],
  'sound.crackle.rateMin': [0, 60, 1],
  'sound.crackle.rateMax': [0, 400, 1],
  'sound.crackle.durMin': [0.0005, 0.02, 0.0005],
  'sound.crackle.durMax': [0.0005, 0.03, 0.0005],
  'sound.crackle.hpf': [200, 10000, 50],
  'sound.penetrate.gain': [0, 1, 0.01],
  'sound.penetrate.freq': [100, 8000, 10],
  'sound.penetrate.dur': [0.005, 0.2, 0.005],
  'sound.complete.gain': [0, 1, 0.01],
  'sound.complete.freq': [100, 8000, 10],
  'sound.complete.dur': [0.005, 0.3, 0.005],
  'sound.friction.gain': [0, 1, 0.01],
  'sound.friction.speedForMax': [2, 40, 0.5],
  'sound.friction.freqMin': [100, 8000, 10],
  'sound.friction.freqMax': [100, 12000, 10],
  'sound.friction.q': [0.3, 8, 0.1],
  'sound.friction.attack': [0.001, 0.05, 0.001],
  'sound.friction.release': [0.001, 0.1, 0.001],
  'sound.click.gain': [0, 1, 0.01],
  'sound.click.dur': [0.001, 0.02, 0.0005],
  'sound.click.hpf': [200, 10000, 50],
  'sound.thud.gain': [0, 1, 0.01],
  'sound.thud.freq': [30, 400, 1],
  'sound.thud.dur': [0.02, 0.4, 0.005],
  'sound.thud.noise': [0, 1, 0.01],
  'sound.touch.gain': [0, 0.5, 0.005],
  'sound.touch.dur': [0.001, 0.03, 0.001],
  'sound.page.gain': [0, 1, 0.01],
  'sound.page.speedForMax': [2, 60, 0.5],
  'sound.page.freqMin': [100, 8000, 10],
  'sound.page.freqMax': [100, 12000, 10],
  'sound.page.q': [0.3, 8, 0.1],
  'sound.page.attack': [0.001, 0.05, 0.001],
  'sound.page.release': [0.001, 0.1, 0.001],
  'sound.pageSlap.gain': [0, 1, 0.01],
  'sound.pageSlap.freq': [30, 400, 1],
  'sound.pageSlap.dur': [0.02, 0.4, 0.005],
  'sound.pageSlap.noise': [0, 1, 0.01],
  'sound.rare.gain': [0, 1, 0.01],
  'sound.rare.freqMin': [50, 4000, 10],
  'sound.rare.freqMax': [100, 12000, 10],
  'sound.rare.qMin': [0.3, 12, 0.1],
  'sound.rare.qMax': [0.3, 20, 0.1],
  'sound.rare.toneGain': [0, 0.6, 0.005],
  'sound.rare.toneMin': [30, 500, 1],
  'sound.rare.toneMax': [50, 1500, 1],
  'sound.rare.pulseRate': [0, 16, 0.1],
  'sound.rare.pulseDepth': [0, 1, 0.01],
  'sound.rare.attack': [0.005, 0.4, 0.005],
  'sound.rare.release': [0.005, 0.5, 0.005],
  'sound.rare.curve': [0.5, 4, 0.1],
  'sound.reveal.gain': [0, 1, 0.01],
  'sound.reveal.freq': [200, 2000, 1],
  'sound.reveal.dur': [0.1, 3, 0.05],
  'sound.reveal.glide': [0.8, 1.5, 0.01],
  'sound.reveal.shimmer': [0, 2, 0.01],
  'sound.reveal.shimmerDur': [0.01, 0.5, 0.005],
  'sound.reveal.hpf': [500, 12000, 50],
  'haptic.penetrate': [0, 40, 1],
  'haptic.complete': [0, 40, 1],
  'haptic.slip': [0, 40, 1],
  'haptic.land': [0, 40, 1],
  'haptic.cutPulse': [2, 60, 1],
  'haptic.cutRateMin': [1, 80, 1],
  'haptic.cutRateMax': [1, 80, 1],
  'haptic.cutSpeedForMax': [2, 40, 0.5],
  'haptic.cutMinSpeed': [0, 6, 0.1],
  'haptic.rareMinRank': [0, 3, 1],
  'haptic.rareFrom': [0, 0.9, 0.01],
  'haptic.rareTo': [0.1, 1, 0.01],
  'haptic.rarePulse': [2, 80, 1],
  'haptic.rareRate': [1, 90, 1],
  // iOS 네이티브 전용. 실기기에서 맞춰야 하는 값들이라 패널에 노출한다.
  'haptic.iosLightMax': [1, 40, 1],
  'haptic.iosMediumMax': [1, 60, 1],
  'haptic.iosMinIntervalMs': [0, 100, 1],
};

export function cloneParams(p: Params): Params {
  return JSON.parse(JSON.stringify(p));
}

/** Deep-assign src into dst, only for keys that already exist in dst (ignores unknown keys). */
export function assignParams(dst: any, src: any): void {
  if (!src || typeof src !== 'object') return;
  for (const k of Object.keys(dst)) {
    if (!(k in src)) continue;
    const d = dst[k], s = src[k];
    if (d && typeof d === 'object' && !Array.isArray(d)) assignParams(d, s);
    else if (typeof d === typeof s) dst[k] = s;
  }
}

/** Live params object. Mutated in place by the panel so every module sees changes instantly. */
export const P: Params = cloneParams(DEFAULT_PARAMS);

// cards.ts 의 faceCount() 가 볼 튜닝 값. **살아 있는 객체**를 넘기므로 패널에서 바꾸면 바로 반영된다.
setFaceShape(P.binder);

// ---------------------------------------------------------------- 등장 연출 색 (슬라이더 없음)
// 등급별 빛 색 (선형 RGB 가산값). 슬라이더로 만들 수 있는 숫자가 아니라 여기 상수로 둔다.
// 인덱스는 CARD_KINDS 와 같은 rank. 없는 등급은 render 가 흰빛으로 떨어진다.
// AR = 금빛(따뜻한 금속), UR = 서늘한 흰-청록빛(프리즘). 세기는 P.reveal 이 정하고 여기는 색조만 준다.
export const REVEAL_TINT: Record<number, [number, number, number]> = {
  2: [1.0, 0.78, 0.36],
  3: [0.62, 0.92, 1.0],
};

// ---------------------------------------------------------------- 바인더 상수 (슬라이더 없음)
// 튜닝 값이 아니라 **구조값·용량값**이라 Params 에 넣지 않는다. "숫자는 params.ts 에" 규칙 때문에 여기 둔다.
export const BINDER_LAYERS = {
  cover: -0.1, // 커버 (맨 뒤)
  sheetBase: 0.0, // 면 안의 z (그 면 그룹 로컬)
  card: 0.02,
  badge: 0.04,
  gloss: 0.05,
  turningLift: 0.2, // 도는 시트 그룹의 z (더미 위)
  stowCard: 0.3, // 수납 중 날아오는 카드
  dim: 0.4, // 상세 보기 디밍 평면 (모든 시트 위, 상세 카드 아래)
  cells: 9,
  facesPerSheet: 2,
  // 커버가 면(시트) 바깥으로 더 나가는 폭 (unit). **시트보다 넉넉해야 한다** —
  // 작으면 페이지가 바인더 밖으로 삐져나온 것처럼 보인다 (2026-09-11 사용자 보고).
  coverMargin: 0.75,
};

// 용량 계획이지 최적화가 아니다 — 창이 없으면 500장에서 GPU 메모리가 터진다 (§7.2).
// 첫 팩. 이후로는 리셋마다 sim.reset() 이 다시 뽑는다.
// (main.ts 가 ?seed 를 읽기 전이라 이 결과는 버려질 수 있다 — 비어 있지 않게 두는 게 목적이다.)
rollCards();
